import { useState } from 'react';
import { Shield, ShieldOff, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { User as UserType } from '@/utils/adminUtils';

interface AdminUserManagementProps {
  users: UserType[];
  onUsersUpdate: (users: UserType[]) => void;
}

export const AdminUserManagement = ({ users, onUsersUpdate }: AdminUserManagementProps) => {
  const [isUpdating, setIsUpdating] = useState<string | null>(null);
  const { toast } = useToast();

  const handleToggleAdmin = async (userId: string, currentIsAdmin: boolean) => {
    setIsUpdating(userId);
    
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ is_admin: !currentIsAdmin })
        .eq('id', userId);

      if (error) throw error;

      // Update local state
      const updatedUsers = users.map(user => 
        user.id === userId 
          ? { ...user, isAdmin: !currentIsAdmin }
          : user
      );
      onUsersUpdate(updatedUsers);

      toast({
        title: "Success",
        description: `User ${!currentIsAdmin ? 'promoted to' : 'removed from'} admin successfully.`,
      });
    } catch (error) {
      console.error('Error updating admin status:', error);
      toast({
        title: "Error",
        description: "Failed to update admin status. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUpdating(null);
    }
  };

  const adminUsers = users.filter(user => user.isAdmin);
  const regularUsers = users.filter(user => !user.isAdmin);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Admin Users
          </CardTitle>
          <CardDescription>
            Users with administrative privileges
          </CardDescription>
        </CardHeader>
        <CardContent>
          {adminUsers.length === 0 ? (
            <p className="text-muted-foreground">No admin users found.</p>
          ) : (
            <div className="space-y-3">
              {adminUsers.map((user) => (
                <div key={user.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <Shield className="h-4 w-4 text-emerald-600" />
                    <div>
                      <div className="font-medium">{user.firstName} {user.lastName}</div>
                      <div className="text-sm text-muted-foreground">{user.email}</div>
                    </div>
                    <Badge variant="secondary">Admin</Badge>
                  </div>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button 
                        variant="outline" 
                        size="sm"
                        disabled={isUpdating === user.id}
                      >
                        <ShieldOff className="h-4 w-4 mr-2" />
                        Remove Admin
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Remove Admin Privileges</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to remove admin privileges from {user.firstName} {user.lastName}? 
                          They will no longer be able to access admin features.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction 
                          onClick={() => handleToggleAdmin(user.id, true)}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                          Remove Admin
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Regular Users</CardTitle>
          <CardDescription>
            Users who can be promoted to admin
          </CardDescription>
        </CardHeader>
        <CardContent>
          {regularUsers.length === 0 ? (
            <p className="text-muted-foreground">No regular users found.</p>
          ) : (
            <div className="space-y-3">
              {regularUsers.map((user) => (
                <div key={user.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="h-4 w-4" /> {/* Placeholder for alignment */}
                    <div>
                      <div className="font-medium">{user.firstName} {user.lastName}</div>
                      <div className="text-sm text-muted-foreground">{user.email}</div>
                    </div>
                    <Badge variant="outline">{user.profileType || 'User'}</Badge>
                  </div>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button 
                        variant="outline" 
                        size="sm"
                        disabled={isUpdating === user.id}
                      >
                        <Shield className="h-4 w-4 mr-2" />
                        Make Admin
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                          <AlertTriangle className="h-5 w-5 text-amber-500" />
                          Grant Admin Privileges
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to grant admin privileges to {user.firstName} {user.lastName}? 
                          This will give them full access to admin features including user management.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction 
                          onClick={() => handleToggleAdmin(user.id, false)}
                        >
                          Grant Admin Access
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};